import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getPlugin, savePluginSettings, togglePlugin } from '../../store/AsyncMethods/PluginsMethod';
import { RESET_PLUGINS_MSG } from '../../store/Types/PluginsTypes';
import backendVerifiedUser from '../../api/backendVerifiedUser';

const Field = ({ label, id, type = 'text', value, onChange, placeholder, helpText, required }) => (
  <div>
    <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-1">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <input
      id={id}
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent shadow-sm"
    />
    {helpText && <p className="mt-1 text-xs text-gray-400">{helpText}</p>}
  </div>
);

const Section = ({ title, children }) => (
  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-5">
    <h2 className="text-base font-semibold text-gray-800 border-b border-gray-100 pb-3">{title}</h2>
    {children}
  </div>
);

export default function EmailPlugin() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { currentPlugin, success, error } = useSelector((s) => s.PluginsReducer);

  const [email, setEmail] = useState('');
  const [appPassword, setAppPassword] = useState('');
  const [fromName, setFromName] = useState('');

  const [saving, setSaving] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testEmailTo, setTestEmailTo] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  const [connectionState, setConnectionState] = useState(null);

  useEffect(() => {
    dispatch(getPlugin('email_smtp'));
  }, []);

  useEffect(() => {
    if (currentPlugin?.plugin_key === 'email_smtp') {
      setEmail(currentPlugin.settings?.email || '');
      setAppPassword(currentPlugin.settings?.app_password || '');
      setFromName(currentPlugin.settings?.from_name || '');
    }
  }, [currentPlugin]);

  useEffect(() => {
    if (success) { toast.success(success); dispatch({ type: RESET_PLUGINS_MSG }); }
    if (error) { toast.error(error); dispatch({ type: RESET_PLUGINS_MSG }); }
  }, [success, error]);

  const handleSave = async () => {
    if (!email.trim() || !appPassword.trim()) {
      toast.error('Email and App Password are required');
      return;
    }
    setSaving(true);
    await dispatch(savePluginSettings('email_smtp', {
      email: email.trim(),
      app_password: appPassword.trim(),
      from_name: fromName.trim(),
    }));
    setSaving(false);
  };

  const handleToggle = async () => {
    const isCurrentlyActive = !!currentPlugin?.is_active;

    if (!isCurrentlyActive) {
      if (!email.trim() || !appPassword.trim()) {
        toast.error('Please enter Email and App Password before activating');
        return;
      }

      // Activating writes only is_active on the backend — it never touches
      // settings. Save first (even if the fields look unchanged) so DB
      // settings always match what's on screen before the plugin goes live;
      // otherwise "Activate" can silently turn the plugin on with an empty
      // settings row if the admin never separately clicked Save Settings.
      const savedOk = await dispatch(savePluginSettings('email_smtp', {
        email: email.trim(),
        app_password: appPassword.trim(),
        from_name: fromName.trim(),
      }));
      if (!savedOk) return;
    }

    setToggling(true);
    await dispatch(togglePlugin('email_smtp'));
    setToggling(false);
  };

  const handleTestConnection = async () => {
    setTesting(true);
    setConnectionState(null);
    try {
      await backendVerifiedUser.post('/plugins/email/test');
      setConnectionState('success');
      toast.success('SMTP login successful!');
    } catch (err) {
      setConnectionState('failed');
      toast.error(err.response?.data?.error?.msg || 'Connection test failed');
    } finally {
      setTesting(false);
    }
  };

  const handleSendTest = async () => {
    if (!testEmailTo.trim()) { toast.error('Enter a recipient email'); return; }
    setSendingTest(true);
    try {
      await backendVerifiedUser.post('/plugins/email/send-test', { to: testEmailTo.trim() });
      toast.success('Test email sent! Check the inbox.');
    } catch (err) {
      toast.error(err.response?.data?.error?.msg || 'Failed to send test email');
    } finally {
      setSendingTest(false);
    }
  };

  const isActive = currentPlugin?.is_active;

  return (
    <div className="mx-3 pb-8 max-w-2xl">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate('/plugins')} className="p-2 rounded-xl hover:bg-gray-100 transition-colors">
          <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <div className="flex items-center gap-3 flex-1">
          <div className="w-10 h-10 rounded-xl bg-red-500 flex items-center justify-center">
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-800">Email (Gmail SMTP)</h1>
            <p className="text-sm text-gray-500">Send order confirmation and other emails to customers</p>
          </div>
        </div>
        <span className={`text-xs font-semibold px-3 py-1.5 rounded-full ${isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
          {isActive ? 'Active' : 'Inactive'}
        </span>
      </div>

      <div className="space-y-5">
        {/* Credentials */}
        <Section title="Gmail Credentials">
          <Field
            label="Gmail Address"
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="e.g. yourstore@gmail.com"
            required
          />
          <Field
            label="App Password"
            id="app_password"
            type="password"
            value={appPassword}
            onChange={(e) => setAppPassword(e.target.value)}
            placeholder="16-character Google App Password"
            helpText="Not your regular Gmail password — generate this from Google Account > Security > App Passwords"
            required
          />
          <Field
            label="From Name"
            id="from_name"
            value={fromName}
            onChange={(e) => setFromName(e.target.value)}
            placeholder="e.g. Paradise Ceramics"
            helpText="Shown as the sender name on emails customers receive"
          />
          <div className="flex gap-3 pt-1">
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex-1 py-2.5 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-xl transition-colors disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save Settings'}
            </button>
            <button
              onClick={handleToggle}
              disabled={toggling}
              className={`flex-1 py-2.5 text-sm font-medium rounded-xl transition-colors disabled:opacity-50 border ${isActive ? 'border-red-200 text-red-600 hover:bg-red-50' : 'border-green-200 text-green-600 hover:bg-green-50'}`}
            >
              {toggling ? '...' : isActive ? 'Deactivate' : 'Activate'}
            </button>
          </div>
        </Section>

        {/* Test Connection */}
        <Section title="Test & Verify">
          <div className="flex gap-3">
            <button
              onClick={handleTestConnection}
              disabled={testing}
              className="flex-1 py-2.5 border border-gray-200 text-gray-700 text-sm font-medium rounded-xl hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              {testing ? 'Checking...' : 'Check Connection'}
            </button>
          </div>
          {connectionState && (
            <div className={`p-3 rounded-xl text-sm font-medium ${connectionState === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
              {connectionState === 'success' ? 'SMTP login successful — credentials are valid.' : 'SMTP login failed — check email/app password above.'}
            </div>
          )}

          <div className="pt-2 border-t border-gray-100">
            <p className="text-sm font-medium text-gray-700 mb-2">Send Test Email</p>
            <div className="flex gap-2">
              <input
                type="email"
                value={testEmailTo}
                onChange={(e) => setTestEmailTo(e.target.value)}
                placeholder="you@example.com"
                className="flex-1 px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
              />
              <button
                onClick={handleSendTest}
                disabled={sendingTest || !isActive}
                className="px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-xl transition-colors disabled:opacity-50"
                title={!isActive ? 'Activate plugin first' : ''}
              >
                {sendingTest ? '...' : 'Send'}
              </button>
            </div>
            {!isActive && <p className="text-xs text-gray-400 mt-1">Activate the plugin first to send test emails.</p>}
          </div>
        </Section>

        {/* Setup Guide */}
        <Section title="Setup Guide">
          <ol className="list-decimal list-inside space-y-2 text-sm text-gray-600">
            <li>Go to your Gmail account and enable <strong>2-Step Verification</strong> (Google Account &gt; Security)</li>
            <li>Go to <strong>Security &gt; App Passwords</strong> and generate a new App Password for "Mail"</li>
            <li>Paste the Gmail address and the 16-character App Password above (not your normal Gmail password)</li>
            <li>Click <strong>Save Settings</strong>, then <strong>Check Connection</strong> to confirm login works</li>
            <li>Click <strong>Activate</strong> to enable order confirmation emails</li>
          </ol>
        </Section>
      </div>
    </div>
  );
}
